import { KeyValue, KeyValuePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, Input, OnChanges, TrackByFunction } from '@angular/core';

import { DisplayType } from '@cds/betting-offer';
import {
    CrmPromoIdsMap,
    EventModel,
    EventOption,
    EventOptionGroup,
    EventParticipant,
    EventParticipantImage,
} from '@frontend/sports/betting-offer/feature/model';
import { ShortNameModule, ShortTeamNamesService } from '@frontend/sports/betting-offer/feature/short-team-names';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { ParticipantImageComponent } from '@frontend/sports/participant-image/feature';
import { OptionPanelThreshold } from '@frontend/sports/types/configuration';
import { orderBy, some } from 'lodash-es';

import { SourceOfPick } from '../betslip-base/models/pick-models';
import { BettingInsightsBindingModel, BettingInsightsComponentLoaderService } from '../betting-insights/betting-insights-component-loader.service';
import { ShowInEpcotDirective } from '../common/epcot-config.directive';
import { BadgeComponent } from '../crm-promotion/badge.component';
import { ComponentProxy } from '../deferred/component-loader.service';
import { ComponentProxyDirective } from '../deferred/component-proxy.directive';
import { MarketHelpComponent } from './market-help/market-help.component';
import { OptionGroup, OptionGroupService } from './option-group.service';
import { OptionNameStrategy } from './option-name.strategy';
import { OptionComponent } from './option.component';
import { PlayerStatsComponent } from './player-stats/player-stats.component';
import { SixPackOptionNameStrategy } from './six-pack-option-name.strategy';

export interface ISixPackOptionRow {
    player: string;
    columns: Map<number, EventOption | null>;
    playerImage?: EventParticipantImage;
    playerId?: number;
}

@Component({
    selector: 'ms-six-pack-option-group',
    templateUrl: './six-pack-option-group.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{ provide: OptionNameStrategy, useClass: SixPackOptionNameStrategy }],
    imports: [
        NgClass,
        NgTemplateOutlet,
        MarketHelpComponent,
        ShowInEpcotDirective,
        BadgeComponent,
        OptionComponent,
        ParticipantImageComponent,
        PlayerStatsComponent,
        KeyValuePipe,
        ComponentProxyDirective,
    ],
})
export class SixPackOptionGroupComponent implements OnChanges {
    @Input() event: EventModel;
    @Input() tracking: string;
    @Input() trackingModuleType = '';
    @Input() optionGroup: EventOptionGroup;
    @Input() thresholds?: OptionPanelThreshold[];
    @Input() isShowMoreDisabled: boolean;
    @Input() showMarketHelp: boolean;
    @Input() crmPromoIdsMap: CrmPromoIdsMap;
    @Input() eventHelpText?: string;
    @Input() sourceOfPick = SourceOfPick.Other;
    @Input() betDetails = '';
    @Input() showLogoColor: boolean;
    @Input() showSixPackParticipantImages = false;
    @Input() showPlayerStats = false;
    @HostBinding('class.with-logos') withLogos = false;

    participant1ImageLoaded = false;
    participant2ImageLoaded = false;
    showParticipantImages = true;
    optionRows: ISixPackOptionRow[];
    optionHeaders: Map<number, string>;
    tupleClass: string;
    maxColumns = 3;
    bettingInsightsComponent: ComponentProxy<BettingInsightsBindingModel>;
    hasInsights: boolean;

    constructor(
        public sitecore: Sitecore,
        private optionGroupService: OptionGroupService,
        private shortTeamNamesService: ShortTeamNamesService,
        private loader: BettingInsightsComponentLoaderService,
    ) {}

    ngOnChanges(changes: ISimpleChanges<SixPackOptionGroupComponent>): void {
        let optionGroups = this.optionGroupService.getOptionGroupsFilteredBySpread(this.optionGroup, (option) => option.optionColumnId);
        optionGroups = orderBy(optionGroups, (o) => o.options[0].order);

        this.optionHeaders = new Map<number, string>();
        this.showParticipantImages = this.showParticipantImages && this.showSixPackParticipantImages;
        this.optionRows = this.getOptionRows(optionGroups);
        this.tupleClass = this.getTupleClass();
        this.setBettingInsights(optionGroups.map((og) => og.optionGroupId));

        if (
            changes.event &&
            (changes.event.firstChange ||
                (changes.event.currentValue && changes.event.previousValue && changes.event.currentValue.id !== changes.event.previousValue.id))
        ) {
            this.withLogos = this.optionGroupService.areImagesAvailable(this.event);
            this.participant1ImageLoaded = this.withLogos;
            this.participant2ImageLoaded = this.withLogos;
        }
    }

    keepOrder(a: KeyValue<unknown, unknown>, b: KeyValue<unknown, unknown>): number {
        return Number(a.key) - Number(b.key);
    }

    trackByPlayer: TrackByFunction<ISixPackOptionRow> = (index, optionLine) => {
        return `${optionLine.player}-${index}`;
    };

    trackByCol: TrackByFunction<KeyValue<unknown, unknown>> = (index, column) => {
        return column?.value instanceof EventOption ? `${column.value.id}-${index}` : `empty-${index}`;
    };

    isMarketPromoted(index: number): boolean {
        return some(
            Array.from(this.optionRows[index].columns.values()),
            (col) =>
                col != null &&
                (!!this.crmPromoIdsMap.markets[col.optionGroupId] || (col.templateId != null && !!this.crmPromoIdsMap.templateIds[col.templateId])),
        );
    }

    isOption(option: unknown): option is EventOption {
        return option instanceof EventOption;
    }

    isOptionPromoted(option: EventOption) {
        return option.id in this.crmPromoIdsMap.options;
    }

    imageLoaded(logoLoaded: boolean, index: number): void {
        if (index === 0) {
            this.participant1ImageLoaded = logoLoaded;
        } else {
            this.participant2ImageLoaded = logoLoaded;
        }

        this.withLogos = this.participant1ImageLoaded && this.participant2ImageLoaded;
    }

    protected getOptionRows(optionGroups: OptionGroup[]): ISixPackOptionRow[] {
        if (
            optionGroups?.some((group) => group.options.every((o) => o.optionPlayerId)) &&
            this.optionGroup.detailedGrouping.displayType === DisplayType.Head2HeadSixPack
        ) {
            this.showParticipantImages = false;

            return [this.getPlayerRow(optionGroups, 0), this.getPlayerRow(optionGroups, 1)];
        }

        const isCompetitionlogosEnabled = this.optionGroupService.hasLogo(this.event.sport.id, this.event.league.id);

        return this.event.participants.map((participant, index) => {
            this.showParticipantImages =
                this.showParticipantImages &&
                (!!(participant.image && participant.image.isParticipantProfile && (participant.image.jersey || participant.image.logo)) ||
                    !!(participant.image?.jersey && !participant.image.logo) ||
                    !!(isCompetitionlogosEnabled && participant.image?.logo));

            return {
                player: this.getPlayerName(participant),
                columns: this.getOptionRowColumns(
                    optionGroups,
                    (options) =>
                        options.find((o) => o.optionPlayerId !== undefined && o.optionPlayerId === participant.fixtureParticipantId) ??
                        options[index],
                ),
                playerImage: participant.image!,
                playerId: participant.id,
            };
        });
    }

    protected getOptionRowColumns(
        optionGroups: OptionGroup[],
        optionSelectFunction: (options: EventOption[]) => EventOption | undefined,
    ): Map<number, EventOption | null> {
        const columns = new Map<number, EventOption | null>();

        optionGroups.slice(0, this.maxColumns).forEach((g) => {
            const option = optionSelectFunction(g.options);
            if (option) {
                const id = option.order ?? option.optionColumnSortOrder ?? option.optionGroupId;
                columns.set(id, option);
                this.optionHeaders.set(id, option.optionColumnName || this.optionHeaders.get(id) || '');
            } else {
                columns.set(g.optionGroupId, null);
            }
        });

        return columns;
    }

    protected getTupleClass(): string {
        const colCount = this.optionRows.length > 0 ? this.optionRows[0].columns.size : 0;
        switch (colCount) {
            case 1:
                return 'double';
            case 2:
                return 'triple';
            case 3:
                return 'quadruple';
            default:
                return '';
        }
    }

    private getPlayerRow(optionGroups: OptionGroup[], index: number): ISixPackOptionRow {
        const lastGroupOption = optionGroups[optionGroups.length - 1].options[index];
        const playerName = this.event.players.find((p) => p.fixtureParticipantId === lastGroupOption.optionPlayerId!)?.name ?? lastGroupOption.name;

        return {
            player: playerName,
            columns: this.getOptionRowColumns(optionGroups, (options) =>
                options.find((option) => option.optionPlayerId === lastGroupOption.optionPlayerId),
            ),
            playerId: lastGroupOption.player1Id!,
        };
    }

    private setBettingInsights(optionGroupIds: number[]): void {
        const insights = this.event.bettingInsights?.marketOptions.filter((mo) => optionGroupIds.includes(mo.marketId)) ?? [];

        if (insights.length) {
            this.bettingInsightsComponent ??= this.loader.getBettingInsightsComponent();
            this.bettingInsightsComponent.update({
                bettingInsightsMarketOptions: insights,
                optionRows: this.optionRows,
                sportId: this.event.sport.id,
            });
        }

        this.hasInsights = !!insights.length;
    }

    private getPlayerName(participant: EventParticipant): string {
        if (!participant) {
            return '';
        }

        if (this.shortTeamNamesService.shouldUseShortName(participant, this.event.sport?.id, this.event.league?.id, ShortNameModule.GameLines)) {
            return participant.shortName!;
        }

        return participant.name;
    }
}
